import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { GetRiderStatsByRiderIdStatusAndDeliverySlot } from '../apollo';
import RideOrderDetailsModal from '../components/RideOrderDetailsModal/RideOrderDetailsModal';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from '@mui/material';
import DataTable from 'react-data-table-component';
import { customStyles } from '../utils/tableRiderStatsPage';
import {GetAllRidersForLocalStore,GetStore} from '../apollo';

const GET_RIDER_STATS = gql`${GetRiderStatsByRiderIdStatusAndDeliverySlot}`;
const GET_ALL_RIDERS = gql`${GetAllRidersForLocalStore}`;
const GET_STORE = gql`${GetStore}` ;

const statuses = {
  '-1': 'All Status', // for dropdown "All"
  0: 'Pending',
  1: 'Started',
  2: 'Ended',
  3: 'Ended by Admin',
};

function RiderStatsPage() {
  const userType = JSON.parse(localStorage.getItem('user-native')); // ✅ Get user info from localStorage

  const [filters, setFilters] = useState({
    riderId: '',
    rideStatus: -1,
    date: '',
    timeSlotId: '',
  });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedRiderStats, setSelectedRiderStats] = useState(null);

  const { data: riderData, loading: ridersLoading } = useQuery(GET_ALL_RIDERS);
  const { data: storeData, loading: storeLoading } = useQuery(GET_STORE, {
    variables: { storeId: userType.storeId }, // ✅ Use logged-in user's storeId
  });

   const { data, loading, refetch } = useQuery(GET_RIDER_STATS, {
    variables: {
      rideStatsInput: {
        riderId: filters.riderId !== '' ? filters.riderId : null,
        rideStatus: filters.rideStatus !== -1 ? filters.rideStatus : null,
        date: filters.date !== '' ? filters.date : null,
        slotId: filters.timeSlotId !== '' ? filters.timeSlotId : null,
      },
      skip: page * limit,
      limit: limit,
    },
    fetchPolicy: 'network-only',
  });

  if (loading || ridersLoading || storeLoading) return <CircularProgress />;

  const riderOptions = riderData && riderData.getAllRidersForLocalStore
  ? riderData.getAllRidersForLocalStore
  : [];
  const timeSlots =
  storeData &&
  storeData.getStore &&
  storeData.getStore.customerDeliverySlot &&
  storeData.getStore.customerDeliverySlot.timeSlot
    ? storeData.getStore.customerDeliverySlot.timeSlot
    : [];

  const rows =
    data &&
    data.getRiderStatsByRiderIdStatusAndDeliverySlot &&
    data.getRiderStatsByRiderIdStatusAndDeliverySlot.rideStat
      ? data.getRiderStatsByRiderIdStatusAndDeliverySlot.rideStat
      : [];

  const handleRideUpdated = async () => {
    await refetch();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage - 1);
  };

  const handlePerRowsChange = (newLimit, newPage) => {
    setLimit(newLimit);
    setPage(newPage - 1);
  };
  const totalCount =
    data &&
    data.getRiderStatsByRiderIdStatusAndDeliverySlot &&
    typeof data.getRiderStatsByRiderIdStatusAndDeliverySlot.totalCount === 'number'
      ? data.getRiderStatsByRiderIdStatusAndDeliverySlot.totalCount
      : 0;

  function formatDateTimeIST(dateString) {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return dateString;

    return `${date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).replace(/\b(am|pm)\b/i, value => value.toUpperCase())} IST`;
  }

  function formatReasonOrderId(orderId) {
    if (!orderId) return 'N/A';

    const orderNumberMatch = orderId.match(/\d+$/);

    return orderNumberMatch ? orderNumberMatch[0] : orderId;
  }

  function getRiderOrderClosedLabel(closedCount, totalCount) {
    const riderOrderLabel = Number(totalCount) === 1 ? 'rider order' : 'rider orders';

    return `${closedCount} of ${totalCount} ${riderOrderLabel} closed.`;
  }

  function formatReasonWithIST(reason) {
    if (!reason) return '-';

    const reassignedMatch = reason.match(/^Order\s+(.+?)\s+(?:was\s+)?reassigned from\s+(.+?)\s+to\s+(.+?)\s+on\s+(.+?)(?:\.\s+Closed\s+(\d+)\s+of\s+(\d+)\s+rider orders?\.?)?$/i);

    if (reassignedMatch) {
      const closedText = reassignedMatch[5] && reassignedMatch[6]
        ? ` ${getRiderOrderClosedLabel(reassignedMatch[5], reassignedMatch[6])}`
        : '';

      return `Order ${formatReasonOrderId(reassignedMatch[1])} was reassigned from ${reassignedMatch[2]} to ${reassignedMatch[3]} on ${formatDateTimeIST(reassignedMatch[4])}.${closedText}`;
    }

    const cancelledAfterOutForDeliveryMatch = reason.match(/^Order\s+(.+?)\s+cancelled after out for delivery on\s+(.+?)\.\s+Closed\s+(\d+)\s+of\s+(\d+)\s+rider orders\.?$/i);

    if (cancelledAfterOutForDeliveryMatch) {
      return `Order ${formatReasonOrderId(cancelledAfterOutForDeliveryMatch[1])} was cancelled after it went out for delivery on ${formatDateTimeIST(cancelledAfterOutForDeliveryMatch[2])}. ${getRiderOrderClosedLabel(cancelledAfterOutForDeliveryMatch[3], cancelledAfterOutForDeliveryMatch[4])}`;
    }

    const deliveredAllMatch = reason.match(/^Rider\s+(.+?)\s+has delivered all\s+(\d+)\s+on\s+(.+)$/i);

    if (deliveredAllMatch) {
      const ordersCount = deliveredAllMatch[2];
      const deliveredAt = formatDateTimeIST(deliveredAllMatch[3]);
      const orderLabel = Number(ordersCount) === 1 ? 'order' : 'orders';

      return `All ${ordersCount} ${orderLabel} delivered on ${deliveredAt}`;
    }

    const startedDeliveryMatch = reason.match(/^Rider\s+(.+?)\s+started Delivery on\s+(.+)$/i);

    if (startedDeliveryMatch) {
      return `Delivery started on ${formatDateTimeIST(startedDeliveryMatch[2])}`;
    }

    const dateMatch = reason.match(/(?:\w{3}\s+\w{3}\s+\d{2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}|(?:\d{1,2}\s+\w{3}\s+\d{4},?\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?\s+GMT[+-]\d{4}(?:\s+\([^)]+\))?))/i);

    if (dateMatch) {
      const dateValue = /^\w{3}\s+\w{3}/.test(dateMatch[0])
        ? `${dateMatch[0]} UTC`
        : dateMatch[0];

      return reason.replace(dateMatch[0], formatDateTimeIST(dateValue));
    }

    return reason;
  }

  const columns = [
    { name: 'Rider Email', selector: row => row.email, sortable: true ,wrap: true},
    //{ name: 'Name', selector: row => row.name, sortable: true },
    { name: 'Delivery Date', selector: row => row.deliveryDateString, sortable: true ,wrap: true},
    { name: 'Started At',   selector: row => new Date(row.startedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }), sortable: true,wrap: true },
    { name: 'Ended At', selector: row => row.endedAt 
    ? new Date(row.endedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) 
    : '-', sortable: true,wrap: true },
   // { name: 'Store', selector: row => row.store, sortable: true },
    { name: 'Orders Count', selector: row => row.ordersCount, sortable: true },
    { name: 'Orders Fulfilled', selector: row => row.ordersFullfilled, sortable: true },
    /*{
      name: 'Status',
      selector: row => {
        const statuses = ['Pending', 'Started', 'Ended', 'Ended by Admin'];
        return statuses[row.rideStatus] || 'Unknown';
      },
      sortable: true,
    },*/
    {
      name: 'Status',
      selector: row => statuses[row.rideStatus] || 'Unknown',
      sortable: true,
    },

    { name: 'Reason',  selector: row => formatReasonWithIST(row.reason), sortable: true,wrap: true, },
    {
      name: 'Action',
      cell: row => (
        <Button
        variant="outlined"
        size="small" // ✅ Reduced size
        sx={{ fontSize: '0.7rem', padding: '2px 6px' }} // ✅ Smaller button
        onClick={() => setSelectedRiderStats(row)}
      >
        View
      </Button>
      ),
    },
  ];

  return (
    <Box p={3}>
      {/* Filters */}
      <Box
        display="flex"
        gap="1rem"
        mb={2}
        sx={{ paddingLeft: '2rem' }}
      >
        {/* Rider Email Dropdown */}
        <Select
          value={filters.riderId}
          onChange={e => setFilters({ ...filters, riderId: e.target.value })}
          displayEmpty
          sx={{
            color: 'black',
            '.MuiSelect-icon': { color: 'black' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                opacity: 1,
                backgroundColor: 'white',
                filter: 'none !important',
                backdropFilter: 'none !important',
                boxShadow: 3,
              },
            },
          }}
        >
          <MenuItem value="" sx={{ color: 'black !important' }}>All Riders</MenuItem>
          {riderOptions.map(rider => (
            <MenuItem
              key={rider._id}
              value={rider._id} // 👈 Send _id to backend
              sx={{ color: 'black !important' }}
            >
              {rider.email} {/* 👀 Show only email */}
            </MenuItem>
          ))}
        </Select>

        {/* Ride Status Dropdown */}
       <Select
          value={filters.rideStatus}
          onChange={e => setFilters({ ...filters, rideStatus: e.target.value })}
          displayEmpty
          sx={{
            color: 'black',
            '.MuiSelect-icon': { color: 'black' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                opacity: 1,
                backgroundColor: 'white',
                filter: 'none !important',
                backdropFilter: 'none !important',
                boxShadow: 3,
              },
            },
          }}
        >
          {Object.entries(statuses).map(([key, label]) => (
            <MenuItem
              key={key}
              value={Number(key)}
              sx={{ color: 'black !important' }}
            >
              {label}
            </MenuItem>
          ))}
        </Select>

        {/* Date Field */}
        <TextField
          label="Date"
          type="date"
          value={filters.date}
          onChange={e => setFilters({ ...filters, date: e.target.value })}
          InputLabelProps={{ shrink: true, style: { color: 'black' } }}
          InputProps={{ style: { color: 'black' } }}
        />

        {/* Time Slot Dropdown */}
        <Select
          value={filters.timeSlotId}
          onChange={e => setFilters({ ...filters, timeSlotId: e.target.value })}
          displayEmpty
          sx={{
            color: 'black',
            '.MuiSelect-icon': { color: 'black' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                opacity: 1,
                backgroundColor: 'white',
                filter: 'none !important',
                backdropFilter: 'none !important',
                boxShadow: 3,
              },
            },
          }}
        >
          <MenuItem value="" sx={{ color: 'black !important' }}>All Time Slots</MenuItem>
          {timeSlots.map(slot => (
            <MenuItem
              key={slot._id}
              value={slot._id} // 👈 Send _id to backend
              sx={{ color: 'black !important' }}
            >
              {slot.start} - {slot.end}
            </MenuItem>
          ))}
        </Select>

        {/* Search Button */}
        <Button
          variant="contained"
          onClick={() => refetch()}
        >
          Search
        </Button>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={rows}
        noHeader
        pagination
        paginationServer
        paginationTotalRows={totalCount}
        paginationDefaultPage={page + 1}
        paginationPerPage={limit}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        highlightOnHover
        customStyles={customStyles}
      />

      {/* Modal */}
      <RideOrderDetailsModal
        open={selectedRiderStats !== null}
        onClose={() => setSelectedRiderStats(null)}
        riderStats={selectedRiderStats}
        onRideUpdated={handleRideUpdated}
      />
    </Box>
  );
}

export default RiderStatsPage;
